/**
 * WACC Coffee Quiz — "Find Your Coffee"
 * Interactive 7-question quiz (5 core + 2 optional) that scores products
 * against user preferences and recommends the top 3 matches.
 */
(function () {
  'use strict';

  const TOTAL_QUESTIONS = 7;
  const CORE_QUESTIONS = 5;

  /* ── Scoring weights (out of 100) ── */
  const WEIGHTS = {
    brew: 20,
    milk: 10,
    flavour: 30,
    adventure: 20,
    morning: 20
  };

  /* ── Flavour keyword mapping ── */
  const FLAVOUR_KEYWORDS = {
    'chocolate-caramel': ['chocolate', 'caramel', 'cocoa', 'toffee', 'fudge', 'brown sugar', 'honey', 'maple', 'molasses', 'dulce'],
    'fruity-berry': ['fruit', 'berry', 'blueberry', 'raspberry', 'cherry', 'strawberry', 'blackberry', 'cranberry', 'grape', 'wine', 'plum'],
    'nutty-toasty': ['nut', 'almond', 'hazelnut', 'walnut', 'pecan', 'cashew', 'toast', 'toasty', 'roasted', 'butter', 'mellow', 'smooth', 'creamy'],
    'floral-delicate': ['floral', 'flower', 'jasmine', 'rose', 'lavender', 'tea', 'delicate', 'light', 'subtle', 'elegant', 'bergamot', 'chamomile'],
    'citrus-bright': ['citrus', 'lemon', 'orange', 'lime', 'grapefruit', 'tangerine', 'bright', 'tangy', 'zesty', 'tropical', 'mango', 'passion'],
    'bold-intense': ['bold', 'intense', 'strong', 'smoky', 'tobacco', 'cedar', 'earthy', 'dark', 'spice', 'spicy', 'pepper', 'cinnamon', 'clove']
  };

  /* ── Brew method to grind mapping ── */
  const BREW_GRIND_MAP = {
    cafetiere: ['cafetiere', 'french-press', 'coarse'],
    espresso: ['espresso', 'fine'],
    filter: ['filter', 'pour-over', 'medium', 'drip'],
    aeropress: ['aeropress', 'medium-fine', 'omni'],
    moka: ['espresso', 'fine', 'moka'],
    coldbrew: ['coarse', 'cold-brew', 'cold brew', 'cafetiere']
  };

  /* ── Adventure slider labels ── */
  const ADVENTURE_LABELS = [
    '', /* 0 unused */
    'I know what I like',
    'Mostly classic, open to a change',
    'Open to suggestions',
    'Love trying new things',
    'Surprise me!'
  ];

  /* ── Personality types ── */
  const PERSONAS = {
    bold_explorer: { name: 'Bold Explorer', emoji: '\u{1F30B}', match: (a) => a.morning === 'quick' && a.adventure >= 4 },
    morning_ritualist: { name: 'Morning Ritualist', emoji: '\u2600\uFE0F', match: (a) => a.morning === 'ritual' && (a.milk === 'black' || a.milk === 'splash') },
    comfort_seeker: { name: 'Comfort Seeker', emoji: '\u{1F9F8}', match: (a) => (a.milk === 'milky' || a.milk === 'sweet') && a.flavour && a.flavour.includes('chocolate-caramel') },
    flavour_adventurer: { name: 'Flavour Adventurer', emoji: '\u{1F30D}', match: (a) => a.adventure >= 3 && a.flavour && (a.flavour.includes('fruity-berry') || a.flavour.includes('floral-delicate') || a.flavour.includes('citrus-bright')) },
    practical_pro: { name: 'Practical Pro', emoji: '\u26A1', match: (a) => a.morning === 'quick' || a.morning === 'onthego' },
    wellness_warrior: { name: 'Wellness Warrior', emoji: '\u{1F33F}', match: (a) => a.wellness && a.wellness.length > 0 && !a.wellness.includes('none') }
  };

  /* ── Frequency recommendation based on volume ── */
  const FREQ_MAP = {
    '1cup': { freq: 'Monthly', text: 'a bag a month' },
    '2-3cups': { freq: 'Every 2 weeks', text: 'a bag every fortnight' },
    '4plus': { freq: 'Weekly', text: 'a bag every week' }
  };

  class WaccQuiz {
    constructor(el) {
      this.el = el;
      this.currentStep = 0;
      this.answers = {};
      this.products = [];
      this.results = [];

      this.parseProducts();
      this.bindEvents();
      this.showStep(0);
    }

    parseProducts() {
      const script = this.el.querySelector('[data-quiz-products]');
      if (script) {
        try {
          this.products = JSON.parse(script.textContent);
        } catch (e) {
          console.warn('WACC Quiz: Could not parse product data', e);
          this.products = [];
        }
      }
    }

    bindEvents() {
      /* Single-select options */
      this.el.querySelectorAll('[data-quiz-option]:not([data-quiz-multi])').forEach(btn => {
        btn.addEventListener('click', () => this.selectOption(btn));
      });

      /* Multi-select toggles */
      this.el.querySelectorAll('[data-quiz-multi]').forEach(btn => {
        btn.addEventListener('click', () => this.toggleMulti(btn));
      });

      /* Adventure slider */
      const slider = this.el.querySelector('[data-quiz-slider="adventure"]');
      if (slider) {
        slider.addEventListener('input', () => this.updateAdventureLabel(slider));
      }

      /* Next / back / skip / restart */
      this.el.querySelectorAll('[data-quiz-next]').forEach(btn => {
        btn.addEventListener('click', () => this.next());
      });
      this.el.querySelectorAll('[data-quiz-back]').forEach(btn => {
        btn.addEventListener('click', () => this.back());
      });
      this.el.querySelectorAll('[data-quiz-skip]').forEach(btn => {
        btn.addEventListener('click', () => this.skip());
      });
      this.el.querySelector('[data-quiz-restart]')?.addEventListener('click', () => this.restart());

      /* Email capture form */
      const emailForm = this.el.querySelector('[data-quiz-email-form]');
      if (emailForm) {
        emailForm.addEventListener('submit', (e) => this.handleEmailCapture(e));
      }

      /* Social share buttons */
      this.el.querySelectorAll('[data-quiz-share]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.shareResults(btn.dataset.quizShare);
        });
      });
    }

    showStep(step) {
      this.currentStep = step;
      const steps = this.el.querySelectorAll('[data-quiz-step]');
      steps.forEach(s => {
        s.classList.toggle('wacc-quiz__step--active', parseInt(s.dataset.quizStep) === step);
      });

      /* Update progress bar */
      const progress = this.el.querySelector('[data-quiz-progress]');
      if (progress && step < TOTAL_QUESTIONS) {
        const displayStep = step < CORE_QUESTIONS ? step + 1 : CORE_QUESTIONS;
        const pct = ((step + 1) / TOTAL_QUESTIONS) * 100;
        progress.style.width = pct + '%';
        progress.setAttribute('aria-valuenow', step + 1);
      }

      const counter = this.el.querySelector('[data-quiz-counter]');
      if (counter && step < TOTAL_QUESTIONS) {
        const isOptional = step >= CORE_QUESTIONS;
        const displayStep = isOptional ? step + 1 : step + 1;
        counter.textContent = `Step ${displayStep} of ${TOTAL_QUESTIONS}${isOptional ? ' (optional)' : ''}`;
      }

      /* Show/hide progress bar on results */
      const progressWrap = this.el.querySelector('.wacc-quiz__progress-wrap');
      if (progressWrap) {
        progressWrap.style.display = step >= TOTAL_QUESTIONS ? 'none' : '';
      }

      /* Scroll + focus */
      requestAnimationFrame(() => {
        this.el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        const activeStep = this.el.querySelector('.wacc-quiz__step--active');
        if (activeStep) {
          const heading = activeStep.querySelector('h2, .wacc-quiz__question, .wacc-quiz__results-heading');
          if (heading) {
            heading.setAttribute('tabindex', '-1');
            heading.focus({ preventScroll: true });
          }
        }
      });
    }

    selectOption(btn) {
      const step = btn.closest('[data-quiz-step]');
      step.querySelectorAll('[data-quiz-option]:not([data-quiz-multi])').forEach(b => b.classList.remove('wacc-quiz__option--selected'));
      btn.classList.add('wacc-quiz__option--selected');
      this.answers[btn.dataset.quizQuestion] = btn.dataset.quizOption;
      /* GA4: fire quiz_start on first interaction */
      if (this.currentStep === 0 && !this._quizStartFired) {
        this._quizStartFired = true;
        document.dispatchEvent(new CustomEvent('wacc:quiz_start'));
      }
      setTimeout(() => this.next(), 300);
    }

    toggleMulti(btn) {
      const step = btn.closest('[data-quiz-step]');
      const selected = step.querySelectorAll('.wacc-quiz__option--selected');
      const isSelected = btn.classList.contains('wacc-quiz__option--selected');

      if (isSelected) {
        btn.classList.remove('wacc-quiz__option--selected');
      } else if (selected.length < 3) {
        btn.classList.add('wacc-quiz__option--selected');
      }

      const question = btn.dataset.quizQuestion;
      const vals = [];
      step.querySelectorAll('.wacc-quiz__option--selected').forEach(b => vals.push(b.dataset.quizOption));
      this.answers[question] = vals;
    }

    updateAdventureLabel(slider) {
      const label = this.el.querySelector('[data-quiz-slider-label]');
      const val = parseInt(slider.value);
      if (label) label.textContent = ADVENTURE_LABELS[val] || '';
      this.answers.adventure = val;
    }

    next() {
      /* Capture slider value if on adventure step */
      if (this.currentStep === 3) {
        const slider = this.el.querySelector('[data-quiz-slider="adventure"]');
        if (slider && this.answers.adventure === undefined) {
          this.answers.adventure = parseInt(slider.value);
        }
      }

      if (this.currentStep < TOTAL_QUESTIONS - 1) {
        this.showStep(this.currentStep + 1);
      } else {
        try {
          this.computeResults();
          this.renderResults();
        } catch (e) {
          console.warn('WACC Quiz: Results error', e);
        }
        this.showStep(TOTAL_QUESTIONS);
      }
    }

    skip() {
      /* Skip optional question — move to next */
      if (this.currentStep < TOTAL_QUESTIONS - 1) {
        this.showStep(this.currentStep + 1);
      } else {
        try {
          this.computeResults();
          this.renderResults();
        } catch (e) {
          console.warn('WACC Quiz: Results error', e);
        }
        this.showStep(TOTAL_QUESTIONS);
      }
    }

    back() {
      if (this.currentStep > 0) {
        this.showStep(this.currentStep - 1);
      }
    }

    restart() {
      this.answers = {};
      this.results = [];
      this.el.querySelectorAll('.wacc-quiz__option--selected').forEach(b => b.classList.remove('wacc-quiz__option--selected'));
      const slider = this.el.querySelector('[data-quiz-slider="adventure"]');
      if (slider) {
        slider.value = 3;
        this.updateAdventureLabel(slider);
      }
      this.showStep(0);
    }

    /** ── Core scoring engine ── */
    computeResults() {
      // Defensive string coercion: list-type metafields serialise as arrays
      // and arrays don't have .toLowerCase(). Coerce every text field to a
      // string here so a metafield-type change doesn't silently break scoring.
      const toStr = (v) => Array.isArray(v) ? v.join(',') : (v == null ? '' : String(v));

      const scored = this.products.map(product => {
        let score = 0;
        let maxScore = 0;
        const productTags = toStr(product.tags).toLowerCase();
        const productTitle = toStr(product.title).toLowerCase();
        const productNotes = toStr(product.flavour_notes).toLowerCase();
        const productDesc = toStr(product.description).toLowerCase();
        const productRoast = toStr(product.roast_level).toLowerCase();
        const searchText = productNotes + ' ' + productDesc + ' ' + productTags;

        /* 1. Brew method match (20 pts) */
        maxScore += WEIGHTS.brew;
        const brewMethod = this.answers.brew || '';
        if (brewMethod && brewMethod !== 'unsure') {
          const grindTerms = BREW_GRIND_MAP[brewMethod] || [];
          if (grindTerms.some(t => productTags.includes(t) || productTitle.includes(t))) {
            score += WEIGHTS.brew;
          } else if (productTags.includes('whole-bean') || productTags.includes('whole bean')) {
            score += WEIGHTS.brew * 0.6;
          } else {
            score += WEIGHTS.brew * 0.2;
          }
        } else {
          score += WEIGHTS.brew * 0.5;
        }

        /* 2. Milk/take preference → roast match (10 pts) */
        maxScore += WEIGHTS.milk;
        const milk = this.answers.milk || '';
        if (milk === 'black') {
          if (productTags.includes('roast-light') || productTags.includes('light-roast') || productRoast.includes('light')) {
            score += WEIGHTS.milk;
          } else if (productTags.includes('roast-medium') || productRoast.includes('medium')) {
            score += WEIGHTS.milk * 0.7;
          } else {
            score += WEIGHTS.milk * 0.3;
          }
        } else if (milk === 'splash') {
          if (productTags.includes('roast-medium') || productRoast.includes('medium')) {
            score += WEIGHTS.milk;
          } else {
            score += WEIGHTS.milk * 0.5;
          }
        } else if (milk === 'milky' || milk === 'sweet') {
          if (productTags.includes('roast-dark') || productTags.includes('dark-roast') || productRoast.includes('dark')) {
            score += WEIGHTS.milk;
          } else if (productTags.includes('roast-medium') || productRoast.includes('medium')) {
            score += WEIGHTS.milk * 0.7;
          } else {
            score += WEIGHTS.milk * 0.3;
          }
        } else {
          score += WEIGHTS.milk * 0.5;
        }

        /* 3. Flavour profile match (30 pts) */
        maxScore += WEIGHTS.flavour;
        const selectedFlavours = this.answers.flavour || [];
        if (selectedFlavours.length > 0) {
          let flavourHits = 0;
          selectedFlavours.forEach(flavourKey => {
            const keywords = FLAVOUR_KEYWORDS[flavourKey] || [];
            if (keywords.some(kw => searchText.includes(kw))) {
              flavourHits++;
            }
          });
          score += (flavourHits / selectedFlavours.length) * WEIGHTS.flavour;
        } else {
          score += WEIGHTS.flavour * 0.5;
        }

        /* 4. Adventure level (20 pts) */
        maxScore += WEIGHTS.adventure;
        const adventure = this.answers.adventure;
        if (adventure !== undefined) {
          if (adventure <= 2) {
            /* Prefer blends */
            if (productTags.includes('origin-blend') || productTags.includes('blend')) {
              score += WEIGHTS.adventure;
            } else {
              score += WEIGHTS.adventure * 0.4;
            }
          } else if (adventure >= 4) {
            /* Prefer single origins */
            if (productTags.includes('origin-single') || productTags.includes('single-origin')) {
              score += WEIGHTS.adventure;
            } else {
              score += WEIGHTS.adventure * 0.4;
            }
          } else {
            /* adventure === 3, even mix */
            score += WEIGHTS.adventure * 0.7;
          }
        } else {
          score += WEIGHTS.adventure * 0.5;
        }

        /* 5. Morning routine (20 pts) */
        maxScore += WEIGHTS.morning;
        const morning = this.answers.morning || '';
        if (morning === 'quick' || morning === 'onthego') {
          /* Dark roast, bold, espresso blends */
          if (productTags.includes('roast-dark') || productTags.includes('dark-roast') || productRoast.includes('dark') || searchText.includes('bold') || searchText.includes('strong')) {
            score += WEIGHTS.morning;
          } else {
            score += WEIGHTS.morning * 0.4;
          }
        } else if (morning === 'ritual') {
          /* Light-medium, complex, single origin */
          if (productTags.includes('roast-light') || productTags.includes('light-roast') || productRoast.includes('light') || productTags.includes('single-origin')) {
            score += WEIGHTS.morning;
          } else if (productTags.includes('roast-medium') || productRoast.includes('medium')) {
            score += WEIGHTS.morning * 0.7;
          } else {
            score += WEIGHTS.morning * 0.3;
          }
        } else if (morning === 'shared') {
          /* Crowd-pleasers: medium roast, blends */
          if (productTags.includes('roast-medium') || productRoast.includes('medium') || productTags.includes('blend')) {
            score += WEIGHTS.morning;
          } else {
            score += WEIGHTS.morning * 0.5;
          }
        } else {
          score += WEIGHTS.morning * 0.5;
        }

        /* Bonus: wellness tag match */
        const wellness = this.answers.wellness || [];
        if (wellness.length > 0 && !wellness.includes('none')) {
          if (productTags.includes('wellness') || productTags.includes('mushroom') || productTags.includes('functional')) {
            score += 5;
            maxScore += 5;
          } else {
            maxScore += 5;
          }
        }

        const pct = maxScore > 0 ? Math.round((score / maxScore) * 100) : 50;
        return { ...product, matchScore: Math.min(pct, 99) };
      });

      scored.sort((a, b) => b.matchScore - a.matchScore);
      this.results = scored.slice(0, 3);

      if (this.results.length === 0 && this.products.length > 0) {
        this.results = this.products.slice(0, 3).map(p => ({ ...p, matchScore: 75 }));
      }
    }

    renderResults() {
      const container = this.el.querySelector('[data-quiz-results]');
      if (!container) return;

      /* Build persona */
      const persona = this.buildPersona();
      const personaEl = this.el.querySelector('[data-quiz-persona]');
      if (personaEl) {
        personaEl.innerHTML = `<span class="wacc-quiz__persona-badge"><span class="wacc-quiz__persona-emoji">${persona.emoji}</span> You're a ${persona.name}!</span>`;
      }

      /* Render product cards */
      container.innerHTML = this.results.map(product => `
        <div class="wacc-quiz__result-card">
          <div class="wacc-quiz__result-match">
            <span class="wacc-quiz__result-match-pct">${product.matchScore}%</span>
            <span class="wacc-quiz__result-match-label">match</span>
          </div>
          ${product.image ? `
            <a href="${product.url}" class="wacc-quiz__result-img-link">
              <img src="${product.image}" alt="${this.escapeHtml(product.title)}" class="wacc-quiz__result-img" loading="lazy" width="300" height="300">
            </a>
          ` : `
            <div class="wacc-quiz__result-img-placeholder"></div>
          `}
          <div class="wacc-quiz__result-info">
            <h3 class="wacc-quiz__result-title">
              <a href="${product.url}">${this.escapeHtml(product.title)}</a>
            </h3>
            ${product.flavour_notes ? `
              <div class="wacc-quiz__result-tags">
                ${(Array.isArray(product.flavour_notes) ? product.flavour_notes : String(product.flavour_notes).split(',')).slice(0, 3).map(n =>
                  `<span class="wacc-quiz__result-tag">${this.escapeHtml(String(n).trim())}</span>`
                ).join('')}
              </div>
            ` : ''}
            <div class="wacc-quiz__result-pricing">
              ${product.subscription_price ? `
                <span class="wacc-quiz__result-price wacc-quiz__result-price--sub">${product.subscription_price}</span>
                <span class="wacc-quiz__result-price wacc-quiz__result-price--original">${product.price}</span>
              ` : `
                <span class="wacc-quiz__result-price">${product.price}</span>
              `}
            </div>
            ${product.subscription_price ? '<p class="wacc-quiz__result-price-note">Subscribe &amp; save 10%</p>' : ''}
            <a href="${product.url}" class="wacc-btn wacc-btn--primary wacc-btn--sm wacc-quiz__result-atc">View &amp; Add to Cart</a>
          </div>
        </div>
      `).join('');

      /* Frequency recommendation */
      const freqEl = this.el.querySelector('[data-quiz-freq-rec]');
      const volume = this.answers.volume;
      if (freqEl && volume && FREQ_MAP[volume]) {
        const rec = FREQ_MAP[volume];
        freqEl.innerHTML = `Based on your intake, we'd recommend <strong>${rec.freq}</strong> delivery &mdash; roughly ${rec.text}.`;
        freqEl.style.display = '';
      }

      /* Update share text */
      const shareText = this.el.querySelector('[data-quiz-share-text]');
      if (shareText) {
        shareText.textContent = `I'm a ${persona.name}! Find your coffee match:`;
      }

      /* GA4: fire quiz_complete */
      document.dispatchEvent(new CustomEvent('wacc:quiz_complete', {
        detail: {
          personality: persona.name,
          products: this.results.map(r => r.title).join(', ')
        }
      }));
    }

    buildPersona() {
      const a = {
        ...this.answers,
        adventure: this.answers.adventure || 3
      };

      /* Check personas in priority order */
      if (PERSONAS.wellness_warrior.match(a)) return PERSONAS.wellness_warrior;
      if (PERSONAS.bold_explorer.match(a)) return PERSONAS.bold_explorer;
      if (PERSONAS.morning_ritualist.match(a)) return PERSONAS.morning_ritualist;
      if (PERSONAS.comfort_seeker.match(a)) return PERSONAS.comfort_seeker;
      if (PERSONAS.flavour_adventurer.match(a)) return PERSONAS.flavour_adventurer;
      if (PERSONAS.practical_pro.match(a)) return PERSONAS.practical_pro;

      /* Fallback based on strongest signal */
      if (a.adventure >= 4) return PERSONAS.flavour_adventurer;
      if (a.milk === 'black') return PERSONAS.morning_ritualist;
      return PERSONAS.practical_pro;
    }

    handleEmailCapture(e) {
      e.preventDefault();
      const form = e.target;
      const email = form.querySelector('input[type="email"]').value;
      const listId = form.dataset.klaviyoList || '';

      if (!email) return;

      const persona = this.buildPersona();

      if (window._learnq && listId) {
        window._learnq.push(['identify', { '$email': email }]);
        window._learnq.push(['track', 'Quiz Completed', {
          persona: persona.name,
          results: this.results.map(r => r.title),
          matchScores: this.results.map(r => r.matchScore),
          answers: this.answers
        }]);
      }

      const successEl = form.closest('.wacc-quiz__email-capture').querySelector('[data-quiz-email-success]');
      if (successEl) {
        form.style.display = 'none';
        successEl.style.display = 'block';
      }

      /* GA4: fire quiz_email_capture */
      document.dispatchEvent(new CustomEvent('wacc:quiz_email_capture', {
        detail: { personality: persona.name }
      }));
    }

    shareResults(platform) {
      const persona = this.buildPersona();
      const text = `I'm a ${persona.name}! Find your perfect coffee match`;
      const url = window.location.href.split('?')[0];
      const fullText = `${text} \u2192 ${url}`;

      switch (platform) {
        case 'whatsapp':
          window.open(`https://wa.me/?text=${encodeURIComponent(fullText)}`, '_blank', 'noopener');
          break;
        case 'facebook':
          window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`, '_blank', 'noopener,width=600,height=400');
          break;
        case 'twitter':
          window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank', 'noopener,width=600,height=400');
          break;
        case 'copy':
          navigator.clipboard.writeText(fullText).then(() => {
            const btn = this.el.querySelector('[data-quiz-share="copy"]');
            if (btn) {
              const originalHTML = btn.innerHTML;
              btn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 6L9 17l-5-5"/></svg>';
              setTimeout(() => { btn.innerHTML = originalHTML; }, 2000);
            }
          }).catch(() => {});
          break;
      }
    }

    escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }
  }

  /* ── Initialise ── */
  function init() {
    document.querySelectorAll('[data-wacc-quiz]').forEach(el => {
      new WaccQuiz(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
